import React, { useId } from "react";
import { Trash2, Tag, Library, Link as LinkIcon, X } from "lucide-react";
import IconButton from "../../components/primitives/IconButton";

function InlineNumeric({ label, value, onChange, step = 0.5, min = 0, placeholder, ariaLabel }) {
    const id = useId();
    const handle = (e) => onChange?.(e.target.value);
    return (
        <label className="mrow__num" htmlFor={id}>
            <span className="mrow__num-label" aria-hidden="true">
                {label}
            </span>
            <input
                id={id}
                type="number"
                inputMode="decimal"
                step={step}
                min={min}
                value={value ?? ""}
                placeholder={placeholder}
                onChange={handle}
                className="mrow__num-input mono-num focus-ring"
                aria-label={ariaLabel}
            />
        </label>
    );
}

function ManifestRow({
    row,
    swatch,
    onChange,
    onRemove,
    onOpenPicker,
    onUnlinkSku,
    canRemove,
    errors,
}) {
    const invalid = !!(errors?.dim || errors?.fit);
    const linked = !!row.sku;
    const diverged =
        linked &&
        (Number(row.w) !== Number(row.sku.w) ||
            Number(row.h) !== Number(row.sku.h) ||
            Number(row.d) !== Number(row.sku.d) ||
            (row.sku.name && row.name !== row.sku.name));

    return (
        <div className={`mrow ${invalid ? "mrow--invalid" : ""}`}>
            <div className="mrow__head">
                <span
                    className="mrow__swatch"
                    style={{ background: swatch }}
                    aria-hidden="true"
                />
                <Tag size={12} className="mrow__tag-icon" aria-hidden="true" />
                <input
                    type="text"
                    className="mrow__name focus-ring"
                    placeholder={row.id}
                    value={row.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    aria-label={`Name for ${row.id}`}
                    maxLength={64}
                />
                <IconButton
                    icon={Library}
                    label="Pick from catalog"
                    size="sm"
                    onClick={onOpenPicker}
                />
                <IconButton
                    icon={Trash2}
                    label={`Remove ${row.name || row.id}`}
                    size="sm"
                    tone="danger"
                    onClick={onRemove}
                    disabled={!canRemove}
                />
            </div>

            {linked && (
                <div
                    className={`mrow__sku-badge ${diverged ? "mrow__sku-badge--diverged" : ""}`}
                    role="status"
                    title={
                        diverged
                            ? "This row started from a saved SKU but has been edited."
                            : "Linked to saved SKU."
                    }
                >
                    <LinkIcon size={10} aria-hidden="true" />
                    <span className="mono">
                        {row.sku.sku_code || "saved SKU"}
                    </span>
                    {diverged && (
                        <span className="mrow__sku-modified mono">modified</span>
                    )}
                    <button
                        type="button"
                        className="mrow__sku-unlink"
                        onClick={onUnlinkSku}
                        aria-label="Unlink from catalog"
                        title="Unlink"
                    >
                        <X size={10} aria-hidden="true" />
                    </button>
                </div>
            )}

            <div className="mrow__fields">
                <InlineNumeric
                    label="W"
                    ariaLabel="Width (inches)"
                    value={row.w}
                    onChange={(v) => onChange("w", v)}
                />
                <InlineNumeric
                    label="H"
                    ariaLabel="Height (inches)"
                    value={row.h}
                    onChange={(v) => onChange("h", v)}
                />
                <InlineNumeric
                    label="D"
                    ariaLabel="Depth (inches)"
                    value={row.d}
                    onChange={(v) => onChange("d", v)}
                />
                <InlineNumeric
                    label="Q"
                    ariaLabel="Quantity (blank = as many as possible)"
                    step={1}
                    min={1}
                    placeholder="—"
                    value={row.qty}
                    onChange={(v) => onChange("qty", v)}
                />
            </div>

            {errors?.msg && (
                <div className="mrow__error" role="alert">
                    {errors.msg}
                </div>
            )}
        </div>
    );
}

export default ManifestRow;
